import { create } from 'zustand'
import { produce } from 'immer'
import { useWidgetSelectorStore } from './widget-selector'
import { FloatingPlacement, usePanelRegistry } from '@/components/editor/panels/panel-registry'
import { hideSettingsOnWidget, resizeSettings, WIDGET_EDIT_PANEL } from '@/components/editor/widget-editor/settings-size'
import { focusField, QUERY_EDITOR_ID } from '@/components/editor/widget-editor/editor-tabs'
import type { QuerySize } from '@/components/editor/widget-editor/query-card'

/** The widget whose query is open on the page: on the widget itself, or in a floating window */
export const useQueryOverlayStore = create<{
  path: string | null
  size: "overlay" | "floating"
}>()(() => ({ path: null, size: "overlay" }))

/** The query's floating window */
export const QUERY_PANEL = "widget-query"

/** Selects the widget and opens its query on top of it */
export function openQueryOverlay(path: string) {
  removeQueryPanel()
  hideSettingsOnWidget()
  useWidgetSelectorStore.setState({ selectedPath: path, hoveredPath: null })
  useQueryOverlayStore.setState({ path, size: "overlay" })
}

export function closeQueryOverlay() {
  removeQueryPanel()
  useQueryOverlayStore.setState({ path: null })
}

/** Panels keep their open and floating state when unregistered, so the query panel's is set here */
function placeQueryPanel(placement?: FloatingPlacement) {
  usePanelRegistry.setState(produce(st => {
    st.openPanels.add(QUERY_PANEL)
    if (!st.floatingPanels.includes(QUERY_PANEL)) st.floatingPanels.push(QUERY_PANEL)
    if (placement) st.floatingPlacements[QUERY_PANEL] = placement
    else delete st.floatingPlacements[QUERY_PANEL]
  }))
}

function removeQueryPanel() {
  usePanelRegistry.setState(produce(st => {
    st.openPanels.delete(QUERY_PANEL)
    const index = st.floatingPanels.indexOf(QUERY_PANEL)
    if (index !== -1) st.floatingPanels.splice(index, 1)
  }))
}

/** Selects the widget and opens its settings on the Query tab, with the cursor in the query */
export function openWidgetQuery(path: string) {
  openWidgetSettings(path)
  focusField("query", QUERY_EDITOR_ID)
}

/** Moves the widget's query on the widget or into a floating window; `placement` puts the window where it was dragged */
export function resizeQuery(path: string, size: QuerySize, placement?: FloatingPlacement) {
  if (size !== "floating") return openQueryOverlay(path)
  hideSettingsOnWidget()
  placeQueryPanel(placement)
  useWidgetSelectorStore.setState({ selectedPath: path, hoveredPath: null })
  useQueryOverlayStore.setState({ path, size: "floating" })
}

/** Selects the widget and opens its settings: in the "Edit widgets" panel while it shows, else on the widget */
export function openWidgetSettings(path: string) {
  closeQueryOverlay()
  useWidgetSelectorStore.setState({ selectedPath: path, hoveredPath: null })
  const panels = usePanelRegistry.getState()
  if (panels.floatingPanels.includes(WIDGET_EDIT_PANEL)) return panels.bringToFront(WIDGET_EDIT_PANEL)
  if (panels.openPanels.has(WIDGET_EDIT_PANEL)) return
  resizeSettings("overlay")
}
